import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { StatCard } from '../components/StatCard';
import { ReportsSummary } from './ReportsSummary';

function BarChartSimple({ rows, labelKey = 'area', valueKey = 'count' }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <ul className="analytics-bars" aria-label="Chart">
      {rows.map((r) => (
        <li key={r[labelKey]} className="analytics-bars__row">
          <span className="analytics-bars__label">{r[labelKey]}</span>
          <span className="analytics-bars__track">
            <span className="analytics-bars__fill" style={{ width: `${((r[valueKey] || 0) / max) * 100}%` }} />
          </span>
          <span className="analytics-bars__value">{r[valueKey]}</span>
        </li>
      ))}
    </ul>
  );
}

export function ReportsAnalytics() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get('/api/analytics/workforce')
      .then((r) => {
        setData(r.data);
        setErr(false);
      })
      .catch(() => {
        setData(null);
        setErr(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const series = useMemo(() => data?.headcount_series || [], [data]);

  if (loading) return <p className="panel__muted">{t('common.loading')}</p>;
  if (err) {
    return (
      <div className="empty-state empty-state--boxed">
        <p>{t('analytics.errorLoad')}</p>
      </div>
    );
  }
  if (data?.insufficient_data) {
    return <p className="panel__muted">{t('analytics.insufficientData')}</p>;
  }

  return (
    <div className="analytics-stack">
      <header className="panel panel--elevated">
        <h2 className="dash-section-title">{t('analytics.workforceTitle')}</h2>
        <p className="panel__muted text-sm">{t('analytics.workforceLead')}</p>
        <p className="panel__muted text-sm">
          {t('analytics.asOf')}: {data.as_of} · {t('analytics.source')}: {data.data_source}
        </p>
      </header>
      <div className="grid-stats">
        <StatCard label={t('analytics.kpiTotal')} value={data.total_headcount} />
        <StatCard label={t('analytics.kpiActive')} value={data.active_headcount} tone="success" />
        <StatCard label={t('analytics.kpiHires30')} value={data.new_hires_last_30_days} tone="accent" />
      </div>
      <section className="panel panel--elevated">
        <h3 className="panel__title">{t('analytics.byArea')}</h3>
        <BarChartSimple rows={data.by_area || []} />
      </section>
      <section className="panel panel--elevated">
        <h3 className="panel__title">{t('analytics.headcountTrend')}</h3>
        <table className="data-table data-table--compact">
          <thead>
            <tr>
              <th>{t('analytics.period')}</th>
              <th>{t('analytics.headcount')}</th>
            </tr>
          </thead>
          <tbody>
            {series.map((p) => (
              <tr key={p.period}>
                <td>{p.period}</td>
                <td>{p.headcount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

export function Reports() {
  const { t } = useApp();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'summary';

  const setTab = (id) => {
    const next = new URLSearchParams(params);
    next.set('tab', id);
    setParams(next, { replace: true });
  };

  return (
    <div className="page-stack reports-page">
      <header className="reports-hero panel panel--elevated">
        <h1 className="reports-hero__title">{t('reportsPage.title')}</h1>
        <p className="reports-hero__lead">{t('reportsPage.lead')}</p>
      </header>
      <nav className="reports-tabs" aria-label={t('reportsPage.navLabel')}>
        <button type="button" className={tab === 'summary' ? 'is-active' : ''} onClick={() => setTab('summary')}>
          {t('reportsPage.tabSummary')}
        </button>
        <button type="button" className={tab === 'analytics' ? 'is-active' : ''} onClick={() => setTab('analytics')}>
          {t('reportsPage.tabAnalytics')}
        </button>
      </nav>
      {tab === 'analytics' ? <ReportsAnalytics /> : <ReportsSummary />}
    </div>
  );
}
